// src/screens/dashboard/EngineConsoleScreen.jsx
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { FieldControl, ButtonControl } from 'spark-core-dx/components';
import { useGenericManager } from 'spark-core-dx/hooks';
import { ItemServiceFactory } from '../../../services/ItemServiceFactory';
import { useDataset } from '../../../engine/dataset/hooks/useDataset';

// ------------------------------------------------------------------
// Module-level cache so the dropdown options are fetched only once
// ------------------------------------------------------------------
let datasetFieldMdCache = null;
let datasetFieldMdPromise = null;

// ------------------------------------------------------------------
// Normalize ANY select-data shape to [{ Text, Value }]
// ------------------------------------------------------------------
function normalizeOptions(raw) {
  if (!raw) return [];
  let arr = raw;

  if (raw.DataJson || raw.jsonData) arr = raw.DataJson ?? raw.jsonData;
  if (typeof arr === 'string') {
    try { arr = JSON.parse(arr); } catch { /* ignore parse error */ }
  }
  if (arr && typeof arr === 'object' && !Array.isArray(arr) && arr.Items) arr = arr.Items;
  if (!Array.isArray(arr)) return [];

  return arr.map((o) => {
    if (o && typeof o === 'object') {
      const text = o.Text ?? o.Label ?? o.Name ?? String(o.Value ?? o.Id ?? o._id ?? '');
      const value = o.Value ?? o.Id ?? o._id ?? o.value ?? o.key ?? text;
      return { Text: String(text), Value: String(value) };
    }
    return { Text: String(o), Value: String(o) };
  });
}

// ------------------------------------------------------------------
// Dataset selector (no VisualFormContext; loads once; never disabled)
// ------------------------------------------------------------------
function DatasetSelector({ value, onChange }) {
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const visualMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Visual);

  const [fieldMd, setFieldMd] = useState(datasetFieldMdCache);
  const [loading, setLoading] = useState(!datasetFieldMdCache);
  const startedRef = useRef(false);

  React.useEffect(() => {
    if (datasetFieldMdCache) return;
    if (startedRef.current) return;
    startedRef.current = true;

    if (!datasetFieldMdPromise) {
      datasetFieldMdPromise = (async () => {
        const metaResp = await visualMgr.GetMetadata({});
        const items = metaResp?.Items ?? [];
        const first = items[0];
        const fmd = first?.Fields?.find?.(x => x?.FieldName === 'DashboardDatasetId') ?? null;
        if (!fmd) return null;

        const sd = await visualMgr.GetSelectData(fmd);
        const options = normalizeOptions(sd);

        return {
          ...fmd,
          DataJson: JSON.stringify(options),
          Disabled: false,
          IsDisabled: false,
          ReadOnly: false,
        };
      })()
      .then(md => { datasetFieldMdCache = md; return md; })
      .finally(() => { datasetFieldMdPromise = null; });
    }

    let alive = true;
    datasetFieldMdPromise
      .then(md => {
        if (!alive) return;
        setFieldMd(md);
        setLoading(false);
        try {
          const opts = md?.DataJson ? JSON.parse(md.DataJson) : [];
          if ((!value || value === '') && opts.length > 0) {
            onChange?.(opts[0].Value);
          }
        } catch { /* ignore */ }
      })
      .catch(err => {
        console.error('Dataset metadata/select-data load failed:', err);
        if (alive) { setFieldMd(null); setLoading(false); }
      });

    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div style={{ opacity: 0.6, fontStyle: 'italic' }}>Loading dataset list…</div>;
  if (!fieldMd) return <div style={{ opacity: 0.7 }}>No dataset field metadata found.</div>;

  const controlKey = `${fieldMd.FieldName || 'Dataset'}:${fieldMd.DataJson?.length || 0}`;

  return (
    <FieldControl
      key={controlKey}
      fieldMetadata={{...fieldMd, PlaceHolder:"Load all..."}}
      groupId="engine-test"
      value={value ?? ''}
      onChange={(evtOrVal) => {
        const next =
          evtOrVal && typeof evtOrVal === 'object' && 'target' in evtOrVal
            ? evtOrVal.target?.value
            : evtOrVal;
        onChange?.(next || '');
      }}
      disabled={false}
    />
  );
}

// ------------------------------------------------------------------
// Tiny results table
// ------------------------------------------------------------------
function ResultsTable({ rows }) {
  if (!rows || rows.length === 0) return <div style={{ opacity: 0.7 }}>No rows.</div>;
  const columns = Object.keys(rows[0] ?? {});
  return (
    <div style={{ overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={{ textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #eee' }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} style={{ padding: '6px 10px', borderBottom: '1px solid #fafafa', whiteSpace: 'nowrap' }}>
                  {String(r[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------------------------------------------------------------------
// Helper: copy text to clipboard (for History export)
// ------------------------------------------------------------------
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fallback */ }

  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    document.execCommand('copy');
    document.body.removeChild(ta);
    return true;
    // eslint-disable-next-line no-empty
  } catch {}
  return false;
}

function EngineConsoleInner() {
  // dataset manager for ID→dataset resolution (hook uses it internally)
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const datasetMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DashboardDataset);

  // 🔌 New hook with your final exposed API
  const {
    // provider passthrough
    engineReady,
    engineError,
    initEngine,
    statusByDatasetId,
    getEngineInfo,   // available if you want to add a button later
    flushFiles,      // available if you want to add a button later

    // local ui state
    selectedDatasetId, setSelectedDatasetId,
    loadedDataset,
    intervalMs, setIntervalMs,
    queryText, setQueryText,
    lastQueryRows,
    output,
    resolvedTable,

    // history
    history,
    clearHistory,
    getHistoryText,

    // actions
    loadAndRegister,
    registerDataset,     // not used in this screen, but exposed
    onManualRefresh,
    onSetInterval,
    onRunQuery,
    onListDatasets,
    onEngineDiagnostics,
    onReinitDb,
    onCopyDebugBundle,
  } = useDataset({ datasetMgr });

  // Build Quick Queries (uses <|TableName|> if no dataset yet)
  const quickQueryOptions = useMemo(() => {
    const T = resolvedTable || '<|TableName|>';
    const s = (x) => x;
    return [
      { group: 'Basics', items: [
        { key: 'datasetquery', label: 'Dataset Query', sql: loadedDataset?.QueryTemplate ?? 'SELECT * FROM <|TableName|> LIMIT 100' },
        { key: 'sample',       label: 'Sample 10 rows', sql: s(`SELECT * FROM ${T} LIMIT 10;`) },
        { key: 'count',        label: 'Count rows',     sql: s(`SELECT COUNT(*) AS c FROM ${T};`) },
        { key: 'orderfirst',   label: 'First 100 by first column', sql: s(`SELECT * FROM ${T} ORDER BY 1 LIMIT 100;`) },
      ]},
      { group: 'Introspection', items: [
        { key: 'tables', label: 'List tables (info_schema)', sql: s(`SELECT table_name
FROM information_schema.tables
WHERE table_schema = current_schema() AND table_type='BASE TABLE'
ORDER BY 1;`) },
        { key: 'columnsP',  label: 'Show columns (PRAGMA)',      sql: s(`PRAGMA table_info(${T});`) },
        { key: 'columnsIS', label: 'Show columns (info_schema)', sql: s(`SELECT column_name, data_type, is_nullable
FROM information_schema.columns
WHERE table_schema = current_schema() AND table_name = '${T}'
ORDER BY ordinal_position;`) },
        { key: 'columns meta', label: 'Show columns (meta)', sql: s(`SELECT column_name, data_type, is_nullable
FROM information_schema.columns
WHERE table_schema = current_schema() AND table_name = 'dataset_meta'
ORDER BY ordinal_position;`) },
        { key: 'meta', label: 'dataset_meta', sql: s(`SELECT * FROM dataset_meta ORDER BY last_refresh DESC`) },
      ]},
      { group: 'Debug', items: [
        { key: 'explain', label: 'Explain sample query', sql: s(`EXPLAIN SELECT * FROM ${T} LIMIT 25;`) },
      ]},
    ];
  }, [resolvedTable, loadedDataset]);

  const flatQuick = useMemo(() => {
    const out = [];
    for (const g of quickQueryOptions) for (const it of g.items) out.push(it);
    return out;
  }, [quickQueryOptions]);

  const [quickKey, setQuickKey] = useState('');
  const applyQuick = useCallback((mode = 'replace', key) => {
    const item = flatQuick.find(x => x.key === (key ?? quickKey));
    if (!item) return;
    if (mode === 'append') {
      setQueryText(prev => (prev ? `${prev}\n\n${item.sql}` : item.sql));
    } else {
      setQueryText(item.sql);
    }
  }, [quickKey, flatQuick, setQueryText]);

  // -------------------
  // Layout
  // -------------------
  const topGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  };

  const card = {
    display: 'grid',
    gap: 8,
    padding: 12,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    background: '#ffffff',
  };

  return (
    <div style={{ padding: 16, display: 'grid', gap: 4, overflow: 'auto', height: '100vh' }}>
      <h1 style={{ margin: 0, padding: 0 }}>Dataset Engine Console</h1>

      {/* TOP: two columns */}
      <div style={topGridStyle}>
        {/* Left column: Dataset + loading/refresh controls */}
        <div style={card}>
          <DatasetSelector value={selectedDatasetId} onChange={setSelectedDatasetId} />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <ButtonControl
              value="Load & Register"
              type="ok-small"
              onClick={() => loadAndRegister(selectedDatasetId, { refreshOnAdd: true })}
              disabled={!engineReady || !selectedDatasetId}
              disabledTitle={!engineReady ? 'Engine not ready' : !selectedDatasetId ? 'Pick a dataset' : ''}
              minWidth={140}
            />
            <ButtonControl
              value="Register Only"
              type="ok-small"
              onClick={() => loadAndRegister(selectedDatasetId, { refreshOnAdd: false })}
              disabled={!engineReady || !selectedDatasetId}
              disabledTitle={!engineReady ? 'Engine not ready' : !selectedDatasetId ? 'Pick a dataset' : ''}
              minWidth={140}
            />
            <ButtonControl
              value="Manual Refresh"
              type="ok-small"
              onClick={() => onManualRefresh(selectedDatasetId)}
              disabled={!engineReady || !selectedDatasetId}
              disabledTitle={!engineReady ? 'Engine not ready' : !selectedDatasetId ? 'Pick a dataset' : ''}
              minWidth={140}
            />
            <ButtonControl
              value="List Loaded Datasets"
              type="ok-small"
              onClick={onListDatasets}
              disabled={!engineReady}
              disabledTitle={!engineReady ? 'Engine not ready' : ''}
              minWidth={140}
            />
            <ButtonControl
              value="Reinit DB"
              type="ok-small"
              onClick={() => onReinitDb({ mode: 'file', dropOldFile: true, reAdd: false })}
              disabled={!engineReady}
              disabledTitle={!engineReady ? 'Engine not ready' : ''}
              minWidth={160}
            />
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="refresh interval (ms)"
              value={intervalMs}
              onChange={(e) => setIntervalMs(e.target.value)}
              style={{ width: 200 }}
              disabled={!engineReady || !selectedDatasetId}
            />
            <ButtonControl
              value="Set/Clear Plan"
              type="ok-small"
              onClick={() => onSetInterval(selectedDatasetId)}
              disabled={!engineReady || !selectedDatasetId}
              disabledTitle={!engineReady ? 'Engine not ready' : !selectedDatasetId ? 'Pick a dataset' : ''}
              minWidth={140}
            />
          </div>
        </div>

        {/* Right column: Status + debug */}
        <div style={card}>
          {/* Top-right: Engine Diagnostics & Copy Debug Bundle */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
            {!engineReady && typeof initEngine === 'function' && (
              <ButtonControl
                value="Init Engine"
                type="ok-small"
                onClick={() => initEngine()}
                minWidth={120}
              />
            )}
            <ButtonControl
              value="Engine Diagnostics"
              type="standard-small"
              onClick={onEngineDiagnostics}
              minWidth={160}
            />
            <ButtonControl
              value="Copy Debug Bundle"
              type="standard-small"
              onClick={onCopyDebugBundle}
              minWidth={160}
            />
          </div>

          <div style={{ fontSize: '1rem', opacity: 0.75 }}>
            <b>Engine:</b>&nbsp;{engineReady ? 'ready' : 'not ready'}
            {!engineReady && engineError ? (
              <div style={{ marginTop: 6, color: '#b91c1c', whiteSpace: 'pre-wrap' }}>
                {engineError}
              </div>
            ) : null}
          </div>

          {resolvedTable && (
            <div style={{ fontSize: '1rem', opacity: 0.75 }}>
              <b>Resolved table:</b>&nbsp;<code>{resolvedTable}</code>
            </div>
          )}

          <div>
            <label style={{ fontWeight: 600 }}>Dataset Status</label>
            <pre style={{ margin: 0, padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 8 }}>
              {JSON.stringify(
                (selectedDatasetId && statusByDatasetId[selectedDatasetId]) || { state: 'unregistered' },
                null,
                2
              )}
            </pre>
          </div>
        </div>
      </div>

      {/* BOTTOM: full width sections */}
      <div style={{ display: 'grid', gap: 16 }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <label style={{ fontWeight: 600 }}>SQL</label>

            {/* Quick Queries */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <select
                value={quickKey}
                onChange={(e) => { setQuickKey(e.target.value); applyQuick('replace', e.target.value); }}
                disabled={!engineReady}
                style={{ minWidth: 260 }}
                title="Quick Queries"
              >
                <option value="">— Quick queries —</option>
                {quickQueryOptions.map(group => (
                  <optgroup key={group.group} label={group.group}>
                    {group.items.map(it => (
                      <option key={it.key} value={it.key}>{it.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <textarea
            rows={6}
            value={queryText}
            onChange={(e) => setQueryText(e.target.value)}
            placeholder="SELECT * FROM <|TableName|> LIMIT 25"
            style={{
              width: '100%',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12
            }}
            disabled={!engineReady || !selectedDatasetId}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <ButtonControl
              value="Run Query"
              type="ok-small"
              onClick={() => onRunQuery(selectedDatasetId, queryText)}
              disabled={!engineReady || !selectedDatasetId || !queryText.trim()}
              disabledTitle={
                !engineReady ? 'Engine not ready' :
                !selectedDatasetId ? 'Pick a dataset' :
                !queryText.trim() ? 'Enter SQL' : ''
              }
              minWidth={120}
            />
          </div>
        </div>

        <div style={card}>
          <label style={{ fontWeight: 600 }}>Query Results</label>
          <ResultsTable rows={lastQueryRows} />
        </div>

        <div style={card}>
          <label style={{ fontWeight: 600 }}>Output Message</label>
          <pre
            style={{
              margin: 0,
              padding: 12,
              background: output.isError ? '#fff1f2' : '#fdfdfd',
              border: '1px solid #eee',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              color: output.isError ? '#b91c1c' : 'inherit'
            }}
          >
{output.text}
          </pre>
        </div>

        {/* NEW: History panel */}
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <label style={{ fontWeight: 600 }}>History</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <ButtonControl
                value="Copy History"
                type="standard-small"
                onClick={async () => {
                  const ok = await copyToClipboard(getHistoryText());
                  // rely on hook to set output; no direct setter exposed here
                  if (!ok) {
                    console.warn('Failed to copy history');
                  }
                }}
              />
              <ButtonControl
                value="Clear"
                type="standard-small"
                onClick={clearHistory}
              />
            </div>
          </div>

          <div style={{
            maxHeight: 260, overflow: 'auto', border: '1px solid #eee', borderRadius: 8,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: 12, background: '#fafafa', padding: 12
          }}>
            {history.length === 0 ? (
              <div style={{ opacity: 0.7 }}>No history yet.</div>
            ) : (
              history.map((h, i) => (
                <div key={i} style={{ marginBottom: 6, color: h.type === 'error' ? '#b91c1c' : '#111827' }}>
                  <span style={{ opacity: 0.6 }}>[{h.ts}]</span>&nbsp;
                  <b>{h.type.toUpperCase()}:</b>&nbsp;
                  <span>{h.text}</span>
                  {h.datasetId ? <span style={{ opacity: 0.6 }}>&nbsp;(ds:{h.datasetId})</span> : null}
                  {h.meta ? <pre style={{ margin: '4px 0 0', opacity: 0.8 }}>{JSON.stringify(h.meta, null, 2)}</pre> : null}
                </div>
              ))
            )}
          </div>
        </div>

        {loadedDataset && (
          <div style={card}>
            <details>
              <summary>Loaded Dataset (metadata)</summary>
              <pre style={{ margin: 0, padding: 12, background: '#fafafa', border: '1px solid #eee', borderRadius: 8 }}>
{JSON.stringify(loadedDataset, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}

export default function EngineConsoleScreen() {
  // Provider is assumed to be applied higher in your tree (DashboardDomain).
  return <EngineConsoleInner />;
}
